import api from 'services/api';
import { array, enforce, num, nullable, object, runSchema, str } from 'validation';

/**
 * A single data table as returned by nap-backend
 * `GET /tables/:zone_name/:table_name` (routes/tables.js). Re-derived from the
 * backend SELECT, NOT the `tablesSlice` `TableItem` type, which drifted:
 * `table_privileges` was dropped from the table entirely (migration 0244) and
 * is neither returned here nor consumed by the editor, and `table_columns` is a
 * nullable jsonb column. The endpoint also returns `folder_path`, which the
 * editor does not read, so it is dropped by the object parser. `table_columns`
 * is validated to the `{ name, type }` shape the editor uses (it rewrites the
 * whole column set on save). Deeply readonly — validated wire data.
 */
export interface FetchedTable {
  readonly table_id: number;
  readonly table_name: string;
  readonly table_columns: { readonly name: string; readonly type: string }[] | null;
  readonly zone_id: number;
  readonly folder_id: number | null;
  readonly zone_name: string;
}

const tableSchema = object<FetchedTable>({
  table_id: num(),
  table_name: str(),
  table_columns: nullable(array(object<{ name: string; type: string }>({ name: str(), type: str() }))),
  zone_id: num(),
  folder_id: nullable(num()),
  zone_name: str(),
});

/**
 * Fetch a single data table by id (`GET /tables/:table_id`) for the edit form
 * when the list slice is empty (e.g. launched from the Workspace). Load-for-edit
 * is by id for every type (issue #886); tables are still addressed by
 * zone + name for delete / row editing. Boundary-validated (issue #757 step 3);
 * structural failure or a drifted identity (`table_id`) yields null so the
 * caller surfaces a "not found" instead of opening the editor on a degraded
 * record.
 */
export const getTable = async (id: number): Promise<FetchedTable | null> => {
  try {
    const res = await api.get(`/tables/${id}`);
    const result = runSchema(tableSchema, res.data, 'getTable');
    if (!result.ok) return null;
    return enforce(result.value, result.warnings, ['table_id']);
  } catch {
    return null;
  }
};
